import React, { useState } from 'react';
import {
  Box,
  Container,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
  StatusIndicator,
} from '@amzn/awsui-components-react-v3';
import { ErrorBoundary } from 'react-error-boundary';
import { usePanelData } from '../../../hooks/usePanelData';
import {
  QueryScope,
  ScopeType,
  SlaGroupRow,
  SlaPanelData,
  SlaTier,
  SlaTimeWindowControl,
} from '../../../interfaces/panel-data';
import { formatDuration } from '../../../utils/format-duration';
import { Badge, BadgeVariant } from '../Badge';
import { InfoTooltip } from '../InfoTooltip';
import { Legend } from '../Legend';
import { BandBar, budgetText } from './BandBar';
import { DeltaStrip } from './DeltaStrip';
import { countOf, RowUnit } from './sla-row-unit';
import { SlaExceptionTable } from './SlaExceptionTable';
import '../panel-tokens.scss';
import './SlaPanel.scss';

/** Used when the panel's own chrome is not configured, so an unpublished profile still reads sensibly. */
const DEFAULT_TITLE = 'SLA compliance';
const DEFAULT_DESCRIPTION =
  'Whether pipeline work finished inside its SLA budget, and what is overrunning.';

/**
 * Everything that differs between the two measured tiers, so the renderer below takes a name and nothing
 * else. Panel constants because the payload carries headings only for itself and the crawl tier.
 *
 * `rowUnit` is what one indexed document is — a batch at a stage, or one leg of one delta. The two counts
 * come from different definitions over different grains, so rendering both as "rows" made them look like one
 * population measured twice. `flow` is the chain in hop order, which the group rows are ordered
 * alphabetically-by-configuration rather than causally; `flowDetail` is the same statement at length.
 */
const TIER_CHROME = {
  batch: {
    heading: 'Batch staging',
    groupHeader: 'Stage',
    exceptionHeading: 'Batch records over budget',
    freshnessKey: 'batchFreshnessExplanation',
    rowUnit: { one: 'batch', many: 'batches' },
    flowAbout: 'the batch staging chain',
    flow: 'Batch → stage entered → stage completed',
    flowDetail:
      'A batch moves through the configured stages in order, and one row here is one batch at one stage. ' +
      'The clock for a stage starts when the batch enters it and stops when that stage reports terminal, so ' +
      'a batch appears once per stage it has reached and not at all for stages ahead of it. Time a batch ' +
      'spends waiting between stages belongs to neither stage, which is why the stage figures do not sum to ' +
      'the batch end-to-end time.',
  },
  delta: {
    heading: 'EPC delta chain',
    groupHeader: 'Leg',
    exceptionHeading: 'Delta legs over budget',
    freshnessKey: 'deltaFreshnessExplanation',
    rowUnit: { one: 'delta', many: 'deltas' },
    flowAbout: 'the EPC delta chain',
    flow: 'SDO snapshot → EPC delta → SCI delta → DTP Delta',
    flowDetail:
      'A snapshot run emits EPC deltas; each delta owes an SCI delta leg, and a delta whose SCI succeeds ' +
      'then owes a DTP Delta leg. One row here is one leg of one delta, however many workflows that leg ' +
      'ran: the clock starts at the first of them and stops at the last update of the last, so a leg is ' +
      'measured on how long it ran rather than on how long it waited to be triggered. A leg that was due ' +
      'and started nothing at all is still counted, timed from when it fell due. DTP is only ever due after ' +
      'a successful SCI, so a delta whose SCI failed shows one leg and not two.',
  },
} as const;

/**
 * §4.8's per-scope element matrix: drop the position-dependent visuals, keep every ranked list. Named rather
 * than deduced from scope checks spread down the component.
 */
const SCOPE_VARIANT: Record<ScopeType, { deltaStrip: boolean; websiteColumn: boolean }> = {
  WEBSITE: { deltaStrip: true, websiteColumn: false },
  COHORT: { deltaStrip: false, websiteColumn: true },
};

/** In place of a band bar, and never alongside one — see `BandBar`. */
const NO_BUDGET_TEXT = 'No SLA configured';

/**
 * Also in place of the bar, and a different statement from `NO_BUDGET_TEXT`: the budget exists and no row was
 * measured against it. "No SLA configured" here would send a reader to fix a profile that is already set.
 */
const NOTHING_JUDGED_TEXT = 'No rows judged';

/** What the `≥` on a truncated group's count means. Structural, so it is not payload copy. */
const IN_FLIGHT_TRUNCATED =
  'In-flight rows hit their fetch cap, so this count and its bands are a lower bound.';

/**
 * R14 in one string: every tile falls back to this rather than to a zero. `0 of 0 within SLA` rendered as
 * `100%` behind a green badge reads as healthy, and that is production's state today — both MARS definitions
 * are INACTIVE, so nothing is indexed.
 */
const NOTHING_MEASURED = 'Nothing measured';

/** `neutral` is not a shade of green: green asserts a pass, neutral asserts that nothing was judged. */
const BADGE_VARIANTS: Record<SlaGroupRow['badge']['severity'], BadgeVariant> = {
  neutral: 'neutral',
  green: 'success',
  amber: 'warning',
  red: 'error',
};

interface SlaPanelProps {
  scope: QueryScope;
  timeWindow?: string;
}

/**
 * Whether this tier's numbers are claims about SLA compliance at all. Both halves are load-bearing: a tier
 * not in `OK` could not be judged, and one with nothing counted has no population to take a ratio over.
 */
const isMeasured = (tier: SlaTier, total: number): boolean => tier.state === 'OK' && total > 0;

/**
 * The same rule as `format-count.ts`'s `requireFinite`: a missing count on a measured tier is a malformed
 * payload, and `0%` is a false but plausible claim. The boundary below contains the throw.
 */
const requireCount = (count: number, field: string): number => {
  if (!Number.isFinite(count)) {
    throw new Error(`SLA payload carried a measured tier with no ${field}: ${count}`);
  }
  return count;
};

const SlaPanelBody: React.FC<SlaPanelProps> = ({ scope, timeWindow = '7D' }) => {
  // Undefined until the reader picks one, so the payload speaks for the initial state: the response reports
  // the window it was built for, which is not necessarily what was asked for. Once chosen it drives both the
  // request and the control, so the selection does not appear to revert for the round trip it caused.
  const [chosenTimeWindow, setChosenTimeWindow] = useState<string>();

  // COHORT aggregates across every website, so it has nothing to wait for. WEBSITE does: without a
  // websiteId the backend rejects the request rather than guessing, so there is no point sending it.
  const { data, isLoading, error } = usePanelData('SLA_COMPLIANCE', scope, {
    timeWindow: chosenTimeWindow ?? timeWindow,
    enabled: scope.type === 'COHORT' || !!scope.websiteId || !!scope.program,
  });

  /** `value` is a string because the honest value is often not a number — see `NOTHING_MEASURED`. */
  const renderTile = (testId: string, heading: string, value: string, detail: string) => (
    <div className="ovp-sla__tile" data-testid={testId}>
      <div className="ovp-sla__tile-heading">{heading}</div>
      <div className="ovp-sla__tile-value">{value}</div>
      <div className="ovp-sla__tile-detail">{detail}</div>
    </div>
  );

  const renderTiles = (payload: SlaPanelData) => {
    const { batchWithinSla, deltaChain, worstOverrun } = payload.tiles;
    const batchMeasured = isMeasured(payload.batch, batchWithinSla.total);
    const deltaMeasured = isMeasured(payload.delta, deltaChain.total);

    const batchTotal = batchMeasured ? requireCount(batchWithinSla.total, 'batch total') : 0;
    const batchWithin = batchMeasured ? requireCount(batchWithinSla.within, 'batch within') : 0;

    // Floored, not rounded: 411 of 412 rounds to `100%` above a detail line reading "411 of 412" and a badge
    // reading "1 breaching". Only a genuine 100% may render as 100%.
    const batchWithinPercent = batchMeasured ? Math.floor((batchWithin / batchTotal) * 100) : 0;

    return (
      <div className="ovp-sla__tiles">
        {batchMeasured
          ? renderTile(
              'sla-tile-batches-within',
              'Batches within SLA',
              `${batchWithinPercent}%`,
              `${batchWithin.toLocaleString()} of ${batchTotal.toLocaleString()} within SLA`
            )
          : renderTile(
              'sla-tile-batches-within',
              'Batches within SLA',
              NOTHING_MEASURED,
              'No batch stage has been judged against a budget.'
            )}

        {deltaMeasured
          ? renderTile(
              'sla-tile-delta-chain',
              TIER_CHROME.delta.heading,
              requireCount(deltaChain.breaching, 'delta breaching').toLocaleString(),
              `breaching, of ${requireCount(deltaChain.total, 'delta total').toLocaleString()} deltas`
            )
          : renderTile(
              'sla-tile-delta-chain',
              TIER_CHROME.delta.heading,
              NOTHING_MEASURED,
              'No delta leg has been judged against a budget.'
            )}

        {/* Three states, not two: "No group is over budget" is true of an empty payload and false of the
            world, so a tier that measured nothing must not borrow it. */}
        {!batchMeasured && !deltaMeasured
          ? renderTile(
              'sla-tile-worst-overrun',
              'Worst overrun',
              NOTHING_MEASURED,
              'Nothing was measured, so there is no overrun to report.'
            )
          : worstOverrun
            ? renderTile(
                'sla-tile-worst-overrun',
                'Worst overrun',
                formatDuration(worstOverrun.overByMs),
                `over budget at ${groupDisplayName(payload, worstOverrun.groupKey)}, against a ${formatDuration(
                  worstOverrun.budgetMs
                )} budget`
              )
            : renderTile(
                'sla-tile-worst-overrun',
                'Worst overrun',
                'None',
                'Every measured group finished inside its budget.'
              )}
      </div>
    );
  };

  /**
   * Heading and explanation, and nothing else: anything resembling a measurement would undo the reason it is
   * on screen, which is that the absence of crawl SLA numbers reads as a decision rather than missing data.
   * Both strings come from the payload, so re-wording the decision is one change rather than two.
   */
  const renderCrawlTier = (payload: SlaPanelData) => (
    <section className="ovp-sla__tier ovp-sla__tier--crawl">
      <h3 className="ovp-sla__tier-heading">{payload.crawlTier.heading}</h3>
      <p className="ovp-sla__tier-note">{payload.crawlTier.explanation}</p>
    </section>
  );

  /**
   * A stage with no budget keeps its count and p95 and loses only the bar: the rows were measured but not
   * judged, and dropping the row would make an unconfigured stage look like an empty one.
   */
  const renderGroup = (group: SlaGroupRow, unit: RowUnit) => (
    <div className="ovp-sla__group" key={group.key}>
      <span className="ovp-sla__group-name">{group.displayName}</span>

      {/* `≥` for the p95's reason: the in-flight fetch caps, so a truncated count is a floor. */}
      <span
        className="ovp-sla__group-total"
        title={group.inFlightTruncated ? IN_FLIGHT_TRUNCATED : undefined}
      >
        {group.inFlightTruncated && '≥ '}
        {countOf(group.total, unit)}
      </span>

      <BandBar
        bands={group.bands}
        rowUnit={unit}
        budgetMs={group.budgetMs}
        noBudgetText={NO_BUDGET_TEXT}
        nothingJudgedText={NOTHING_JUDGED_TEXT}
      />

      {/* `≥` because the ingestion horizon can cut the terminal population short, and an uncensored-looking
          p95 is the most believable wrong number here. The budget on hover for the same reason the bar
          carries it: `p95 26h` in red says nothing about which number it is red against. */}
      {group.p95Ms !== null && (
        <span
          className={`ovp-sla__group-p95 ${group.p95OverBudget ? 'ovp-sla__group-p95--over' : ''}`}
          title={group.budgetMs === null ? undefined : budgetText(group.budgetMs)}
        >
          p95 {group.p95IsCensored && '≥ '}
          {formatDuration(group.p95Ms)}
        </span>
      )}

      {/* The badge is the one part of the row a reader takes in without hovering, so the budget is stated in it
          rather than only on hover: "1 of 1 breaching" says nothing about what it was breaching. */}
      <Badge variant={BADGE_VARIANTS[group.badge.severity]}>
        {group.badge.text}
        {group.budgetMs !== null && ` (${budgetText(group.budgetMs)})`}
      </Badge>

      {/* Verbatim, and in place of the verdict: the fix is "widen the ingestion window", a different action
          from "set a budget". */}
      {group.unenforceableReason && (
        <span className="ovp-sla__group-note">{group.unenforceableReason}</span>
      )}
    </div>
  );

  /**
   * An age rather than a timestamp, because the question is whether the numbers are current. Clamped at zero
   * because the two clocks are the indexer's and the browser's, and `formatDuration` refuses a negative.
   */
  const renderFreshness = (tier: SlaTier, explanation: string | undefined) => {
    if (tier.lastIngestedAtMs === null) return null;
    const age = formatDuration(Math.max(0, Date.now() - tier.lastIngestedAtMs));
    return (
      <p className="ovp-sla__tier-note">
        Last ingested {age} ago.
        {tier.isStale && <span className="ovp-sla__stale"> No longer being updated.</span>}
        {explanation && <InfoTooltip about="ingestion freshness" text={explanation} />}
      </p>
    );
  };

  /**
   * The ranked list or nothing: an empty table is filters over no rows, and the badges above already say that
   * nothing is over budget. `groupNames` comes from this tier's own groups, which keeps raw codes off screen
   * without the table knowing how either tier names things.
   */
  const renderExceptions = (payload: SlaPanelData, tier: SlaTier, name: 'batch' | 'delta') => {
    if (tier.exceptions.length === 0) return null;
    return (
      <SlaExceptionTable
        heading={TIER_CHROME[name].exceptionHeading}
        rows={tier.exceptions}
        exceptionTotal={tier.exceptionTotal}
        exceptionCap={tier.exceptionCap}
        capNote={payload.copy.exceptionCapNote}
        showWebsite={SCOPE_VARIANT[payload.scopeType].websiteColumn}
        groupNames={Object.fromEntries(tier.groups.map((group) => [group.key, group.displayName]))}
        groupHeader={TIER_CHROME[name].groupHeader}
      />
    );
  };

  /**
   * Delta rows with no `website_id` — a missing program-source mapping, not a panel fault. Cohort scope only:
   * a website-scoped search excludes exactly the rows it would count, so its zero would assert none exist.
   */
  const renderUnmappedRows = (tier: SlaTier) =>
    tier.unmappedRows !== null && tier.unmappedRows > 0 ? (
      <p className="ovp-sla__tier-note" data-testid="sla-unmapped-rows">
        {tier.unmappedRows.toLocaleString()} delta rows have no website mapping and are excluded from
        per-website views.
      </p>
    ) : null;

  /**
   * Between the tier's groups and its exception table, because it is the same rows in a third reading. Both
   * guards are meant: the matrix is the decision, and the null is the payload declining to publish cells whose
   * order would mean nothing across websites. `legNames` comes from the tier's own groups, which lets a cell
   * say "DTP: not due yet" without this component knowing a chain has two legs.
   */
  const renderDeltaStrip = (payload: SlaPanelData) => {
    if (!SCOPE_VARIANT[payload.scopeType].deltaStrip || payload.deltaStrip === null) return null;
    return (
      <DeltaStrip
        cells={payload.deltaStrip}
        legNames={payload.delta.groups.map((group) => ({
          key: group.key,
          displayName: group.displayName,
        }))}
        cap={payload.deltaStripCap}
        capNote={payload.copy.deltaStripCapNote}
      />
    );
  };

  /**
   * What the delta clock runs on, labelled and above the rows it explains. Unlabelled between the group rows
   * and the strip it read as a footnote on whichever the reader had looked at last; the sentence describes how
   * a leg's elapsed is derived, so it belongs to what is judged against a budget and not to the strip, which
   * draws outcomes and compares nothing.
   */
  const renderClockNote = (payload: SlaPanelData, name: 'batch' | 'delta') => {
    // Delta-only, because the copy is: a batch stage's clock is described by `flowDetail` instead.
    if (name !== 'delta' || !payload.copy.deltaClockExplanation) return null;
    return (
      <p className="ovp-sla__clock-note" data-testid="sla-delta-clock-note">
        <span className="ovp-sla__clock-label">How each leg is timed:</span>{' '}
        {payload.copy.deltaClockExplanation}
      </p>
    );
  };

  /**
   * What this tier measures and in what order, above its numbers rather than behind an icon. Rendered even
   * when the tier is unavailable: the flow is a claim about the pipeline, not about the index, and an empty
   * tier is when a reader most needs it. Above the group rows because those rows are its hops, and nothing
   * else on screen says which hop follows which.
   */
  const renderFlow = (name: 'batch' | 'delta') => {
    const chrome = TIER_CHROME[name];
    return (
      <p className="ovp-sla__tier-flow" data-testid={`sla-flow-${name}`}>
        <span className="ovp-sla__flow-hops">{chrome.flow}</span>
        <InfoTooltip about={chrome.flowAbout} text={chrome.flowDetail} />
      </p>
    );
  };

  const renderTier = (payload: SlaPanelData, name: 'batch' | 'delta') => {
    const chrome = TIER_CHROME[name];
    const tier = payload[name];
    return (
      <section className={`ovp-sla__tier ovp-sla__tier--${name}`}>
        <h3 className="ovp-sla__tier-heading">{chrome.heading}</h3>
        {renderFlow(name)}
        {/* The payload's own sentence, unaltered: it names what is missing and what to do about it, and a
            paraphrase here would be a second explanation to keep current. */}
        {tier.state !== 'OK' ? (
          tier.unavailableReason && <p className="ovp-sla__tier-reason">{tier.unavailableReason}</p>
        ) : (
          <>
            {renderFreshness(tier, payload.copy[chrome.freshnessKey])}
            {renderUnmappedRows(tier)}
            {renderClockNote(payload, name)}
            <div className="ovp-sla__groups">
              {tier.groups.map((group) => renderGroup(group, chrome.rowUnit))}
            </div>
            {name === 'delta' && renderDeltaStrip(payload)}
            {renderExceptions(payload, tier, name)}
          </>
        )}
      </section>
    );
  };

  /**
   * Rendered only when a tier has groups — with no bands on screen it is a key for colours nothing uses. The
   * two threshold sentences hang off it rather than the tiles because they carry interpolated percentages,
   * which in the empty state would put a figure on a panel that measured nothing.
   */
  const renderLegend = (payload: SlaPanelData) => {
    if (payload.batch.groups.length === 0 && payload.delta.groups.length === 0) return null;
    return (
      <div className="ovp-sla__legend">
        <Legend
          items={[
            { label: 'Within SLA', type: 'within' },
            { label: 'At risk', type: 'at-risk' },
            { label: 'Breaching', type: 'breaching' },
            { label: 'No budget configured', type: 'not-registered' },
          ]}
        />
        {payload.copy.atRiskExplanation && (
          <InfoTooltip about="the at-risk band" text={payload.copy.atRiskExplanation} />
        )}
        {payload.copy.severeBreachExplanation && (
          <InfoTooltip about="breach severity" text={payload.copy.severeBreachExplanation} />
        )}
      </div>
    );
  };

  /** The one thing a reader takes in first, so it stays neutral until something has been measured. */
  const renderBadge = (payload: SlaPanelData) => {
    const { batchWithinSla, deltaChain } = payload.tiles;
    const batchMeasured = isMeasured(payload.batch, batchWithinSla.total);
    const deltaMeasured = isMeasured(payload.delta, deltaChain.total);
    if (!batchMeasured && !deltaMeasured) {
      return <Badge variant="neutral">{NOTHING_MEASURED}</Badge>;
    }
    // Gated per tier and guarded by `requireCount`, as the tiles do it. Read raw, a missing count makes
    // `breaching > 0` false and badges the green "all within SLA"; summed ungated, a stale count on an
    // unmeasured tier inflates the total the reader trusts most.
    const breaching =
      (batchMeasured ? requireCount(batchWithinSla.breaching, 'batch breaching') : 0) +
      (deltaMeasured ? requireCount(deltaChain.breaching, 'delta breaching') : 0);
    return breaching > 0 ? (
      <Badge variant="error">⊘ {breaching.toLocaleString()} breaching</Badge>
    ) : (
      <Badge variant="success">All measured work within SLA</Badge>
    );
  };

  /**
   * Built from the payload's own vocabulary, and hidden when it offers no options: an empty control implies
   * the data is filterable in a way it is not.
   */
  const renderWindowFilter = (control: SlaTimeWindowControl) => {
    if (control.options.length === 0) return null;
    const options: SelectProps.Option[] = control.options.map((option) => ({
      label: option.displayName,
      value: option.value,
    }));
    // `control.value`, not `control.defaultValue`: the first is the window these numbers came from, and
    // showing the fallback beside them would mislabel every figure below.
    const selected = chosenTimeWindow ?? control.value;
    return (
      <label className="ovp-sla__filter">
        <span className="ovp-sla__filter-label">Time window</span>
        <span className="ovp-sla__filter-control">
          <Select
            selectedOption={options.find((option) => option.value === selected) ?? options[0]}
            options={options}
            onChange={(event) => setChosenTimeWindow(event.detail.selectedOption.value || selected)}
            data-testid="sla-filter-time-window"
          />
        </span>
      </label>
    );
  };

  /**
   * In the header for the funnel's reason: the window qualifies every number below it, and as a `Header`
   * action it stays put while the body swaps between loading, error and loaded — a control that vanished
   * mid-request would take the explanation of the spinner with it. Relies on `usePanelData` keeping data.
   */
  const filterControls = data?.filters ? (
    <div className="ovp-sla__filters">{renderWindowFilter(data.filters.timeWindow)}</div>
  ) : null;

  return (
    <Box margin={{ bottom: 'l' }}>
      <Container
        header={
          <Header
            variant="h2"
            description={data?.panel?.description || DEFAULT_DESCRIPTION}
            actions={filterControls}
          >
            <span className="ovp-sla__title">
              <span>{data?.panel?.title || DEFAULT_TITLE}</span>
              {data && renderBadge(data)}
            </span>
          </Header>
        }
      >
        {isLoading ? (
          <SpaceBetween direction="horizontal" size="xs">
            <Spinner />
            <span>Loading SLA data...</span>
          </SpaceBetween>
        ) : error ? (
          <StatusIndicator type="error">Failed to load SLA data.</StatusIndicator>
        ) : data ? (
          <div className="ovp-sla">
            {renderTiles(data)}
            {renderLegend(data)}
            {renderCrawlTier(data)}
            {renderTier(data, 'batch')}
            {renderTier(data, 'delta')}
          </div>
        ) : null}
      </Container>
    </Box>
  );
};

/**
 * The configured wording for a group key, from whichever tier holds it. Falls back to the raw code, which is
 * visible and therefore reportable rather than silently blank.
 */
const groupDisplayName = (payload: SlaPanelData, key: string): string => {
  const group = [...payload.batch.groups, ...payload.delta.groups].find(
    (candidate) => candidate.key === key
  );
  return group?.displayName ?? key;
};

/** Declared outside the component so the boundary is not handed a new component type each render. */
const SlaPanelError: React.FC = () => (
  <Box margin={{ bottom: 'l' }}>
    <Container header={<Header variant="h2">{DEFAULT_TITLE}</Header>}>
      <StatusIndicator type="error">Failed to load SLA data.</StatusIndicator>
    </Container>
  </Box>
);

/**
 * A payload missing a number throws *during render*, which react-query does not catch and
 * `AppInitWrapper.componentDidCatch` rethrows to fail the whole app — so without this boundary one malformed
 * payload white-screens the dashboard.
 *
 * `resetKeys` holds the scope primitives rather than the `scope` object, which callers pass as a fresh
 * literal: keying on the object would reset on every parent render, throw again, and loop.
 */
export const SlaPanel: React.FC<SlaPanelProps> = (props) => (
  <ErrorBoundary
    resetKeys={[props.scope.type, props.scope.websiteId, props.scope.program, props.timeWindow]}
    FallbackComponent={SlaPanelError}
  >
    <SlaPanelBody {...props} />
  </ErrorBoundary>
);
